import { useState, useEffect, useMemo } from "react";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Avatar,
  Button,
  Stack,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  SportsVolleyball,
  FilterAlt,
  Close,
} from "@mui/icons-material";

interface Player {
  id: number;
  nickname: string;
  firstName: string;
  lastName: string;
  gender: string;
  age: number;
  roles: string[];
  attack: number;
  defense: number;
  serve: number;
  wall: number;
  personality: number;
  seniority: number;
  skill: number;
}

interface TeamMember {
  player: Player;
  role: string;
}

interface Formation {
  score: number;
  totalScore: number;
  avgSkillScore: number;
  balanceScore: number;
  genderBalanceScore: number;
  rotationBonus: number;
  avgAge: number;
  avgSeniority: number;
  attack: number;
  defense: number;
  serve: number;
  wall: number;
  personality: number;
  team: TeamMember[];
}

const formationLayout = [
  "CENTRALE",
  "BANDA",
  "PALLEGGIO",
  "CENTRALE",
  "BANDA",
  "OPPOSTO",
];

const roleColors: Record<string, string> = {
  CENTRALE: "#e17055",
  BANDA: "#0984e3",
  PALLEGGIO: "#00b894",
  OPPOSTO: "#d63031",
  LIBERO: "#feca57",
};

export default function FormationResults({
  results,
  players,
}: {
  results: Formation[];
  players: Player[];
}) {
  const [index, setIndex] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [roleFilter, setRoleFilter] = useState<Record<number, string[]>>({});
  const [rotatedPlayers, setRotatedPlayers] = useState<(TeamMember | null)[]>([]);
  const [selectedPlayers, setSelectedPlayers] = useState<number[]>([]);



  // Calcola i ruoli effettivamente presenti nei risultati per ogni player
  const availableRolesByPlayer = useMemo(() => {
    const map: Record<number, Set<string>> = {};
    results.forEach((formation) => {
      formation.team.forEach(({ player, role }) => {
        if (!map[player.id]) map[player.id] = new Set();
        map[player.id].add(role);
      });
    });
    return Object.fromEntries(
      Object.entries(map).map(([id, roles]) => [Number(id), Array.from(roles)])
    );
  }, [results]);

  // Inizializza i filtri con tutti i ruoli effettivi presenti nei risultati
  useEffect(() => {
    const init: Record<number, string[]> = {};
    players.forEach((p) => {
      init[p.id] = availableRolesByPlayer[p.id]
        ? [...availableRolesByPlayer[p.id]]
        : [];
    });
    setRoleFilter(init);
  }, [players, availableRolesByPlayer]);


  const next = () => setIndex((i) => (i + 1) % filteredResults.length);
  const prev = () => setIndex((i) => (i - 1 + filteredResults.length) % filteredResults.length);


  const filteredResults = useMemo(() => {
    return results.filter((formation) => {
      // 1. Filtro per ruolo
      const rolesOk = formation.team.every((tm) => {
        const allowed = roleFilter[tm.player.id];
        return !allowed || allowed.includes(tm.role);
      });

      // 2. Filtro per giocatori selezionati (OR logico)
      const playersOk =
        selectedPlayers.length === 0 ||
        formation.team.some((tm) => selectedPlayers.includes(tm.player.id));

      return rolesOk && playersOk;
    });
  }, [results, roleFilter, selectedPlayers]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [filteredResults]);

  useEffect(() => {
    if (index >= filteredResults.length) {
      setIndex(0);
    }
  }, [filteredResults, index]);

  const orderedPlayers: (TeamMember | null)[] = new Array(6).fill(null);

  useEffect(() => {
    setRotatedPlayers(orderedPlayers);
  }, [filteredResults, index]);

  if (filteredResults.length === 0) {
    return (
      <Typography color="gray" textAlign="center" mt={4}>
        No formations available.
      </Typography>
    );
  }

  const f = filteredResults[index];
  if (!f) return null;


  const used = new Set<number>();
  formationLayout.forEach((role, i) => {
    const nextItem = f.team.find((t, idx) => t.role === role && !used.has(idx));
    if (nextItem) {
      orderedPlayers[i] = nextItem;
      used.add(f.team.indexOf(nextItem));
    }
  });

  const rotateForward = () => {
    setRotatedPlayers((prev) => {
      if (!prev || prev.length === 0) return prev;
      const [first, ...rest] = prev;
      return [...rest, first];
    });
  };

  const rotateBackward = () => {
    setRotatedPlayers((prev) => {
      if (!prev || prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      return [last, ...prev.slice(0, -1)];
    });
  };

  const statsKeys = [
    "totalScore",
    "avgSkillScore",
    "balanceScore",
    "avgAge",
    "avgSeniority",
    "attack",
    "defense",
    "serve",
    "wall",
    "personality",
  ] as const;

  const statsMinMax: Record<(typeof statsKeys)[number], { min: number; max: number }> = {} as any;
  statsKeys.forEach((key) => {
    const values = filteredResults.map((r) => r[key]);
    statsMinMax[key] = { min: Math.min(...values), max: Math.max(...values) };
  });

  const getStatColor = (key: (typeof statsKeys)[number], value: number) => {
    if (value === statsMinMax[key].max) return "limegreen";
    if (value === statsMinMax[key].min) return "tomato";
    return "white";
  };



  const resetFilters = () => {
    const init: Record<number, string[]> = {};
    players.forEach((p) => {
      init[p.id] = availableRolesByPlayer[p.id]
        ? [...availableRolesByPlayer[p.id]]
        : [];
    });
    setRoleFilter(init);
    setSelectedPlayers([]); // ✅ reset giocatori
  };

  return (
    <Box>
      {/* Icona filtro */}
      <Box textAlign="right" mb={1}>
        <Tooltip title="Filters">
          <IconButton onClick={() => setFilterOpen(true)} color="inherit" size="small">
            <FilterAlt />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Navigazione */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Button size="small" variant="outlined" color="inherit" startIcon={<ChevronLeft />} onClick={prev}>
          Prev
        </Button>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} fontSize="0.95rem">
          🏐 Formation {index + 1}/{filteredResults.length}
        </Typography>
        <Button size="small" variant="outlined" color="inherit" endIcon={<ChevronRight />} onClick={next}>
          Next
        </Button>
      </Stack>

      <Stack direction="row" justifyContent="center" spacing={1} mb={1}>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          startIcon={<ChevronLeft />}
          onClick={rotateBackward}
        >
          Rotate Backward
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          endIcon={<ChevronRight />}
          onClick={rotateForward}
        >
          Rotate Forward
        </Button>
      </Stack>

      {/* Campo */}
      <Paper
        sx={{
          p: { xs: 1, md: 2 },
          mb: 2,
          bgcolor: "#0a3d62",
          color: "white",
          borderRadius: 3,
          boxShadow: "0 0 10px rgba(0,0,0,0.3)",
        }}
      >
        <Box
          sx={{
            position: "relative",
            bgcolor: "#1e272e",
            borderRadius: 2,
            overflow: "hidden",
            p: 2,
            "&::before": {
              content: '""',
              position: "absolute",
              top: "50%",
              left: 0,
              width: "100%",
              height: "1px",
              bgcolor: "rgba(255,255,255,0.3)",
              transform: "translateY(-50%)",
            },
          }}
        >
          {/* Riga anteriore */}
          <Grid container justifyContent="space-between" sx={{ position: "relative", zIndex: 1 }}>
            {rotatedPlayers.slice(0, 3).map((p, i) => (
              <Grid key={i}>
                {p && (
                  <Box textAlign="center">
                    <Avatar
                      sx={{
                        bgcolor: roleColors[p.role] || "#b2bec3",
                        mx: "auto",
                        width: { xs: 26, md: 36 },
                        height: { xs: 26, md: 36 },
                        fontSize: { xs: 11, md: 14 },
                        border: "1px solid white",
                      }}
                    >
                      {p.player.nickname.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="caption" display="block" fontSize={{ xs: "0.65rem", md: "0.75rem" }}>
                      {p.player.nickname}
                    </Typography>
                    <Typography variant="caption" color="lightgray" fontSize={{ xs: "0.6rem", md: "0.65rem" }}>
                      {p.role}
                    </Typography>
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>

          {/* Riga posteriore */}
          <Grid container justifyContent="space-between" sx={{ mt: 2, position: "relative", zIndex: 1 }}>
            {rotatedPlayers.slice(3, 6).reverse().map((p, i) => (
              <Grid key={i}>
                {p && (
                  <Box textAlign="center">
                    <Avatar
                      sx={{
                        bgcolor: roleColors[p.role] || "#b2bec3",
                        mx: "auto",
                        width: { xs: 26, md: 36 },
                        height: { xs: 26, md: 36 },
                        fontSize: { xs: 11, md: 14 },
                        border: "1px solid white",
                      }}
                    >
                      {p.player.nickname.charAt(0).toUpperCase()}
                    </Avatar>
                    <Typography variant="caption" display="block" fontSize={{ xs: "0.65rem", md: "0.75rem" }}>
                      {p.player.nickname}
                    </Typography>
                    <Typography variant="caption" color="lightgray" fontSize={{ xs: "0.6rem", md: "0.65rem" }}>
                      {p.role}
                    </Typography>
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
      </Paper>

      {/* Statistiche */}
      <Card
        sx={{
          bgcolor: "#1e272e",
          color: "white",
          borderRadius: 3,
          p: 1,
          boxShadow: "0 0 15px rgba(0,0,0,0.4)",
        }}
      >
        <CardContent sx={{ p: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <SportsVolleyball color="warning" fontSize="small" />
            <Typography variant="subtitle2" sx={{ fontWeight: 400 }} fontSize="0.85rem">
              Team Stats
            </Typography>
          </Stack>

          <Grid container justifyContent="space-between" spacing={1}>
            {statsKeys.map((key) => (
              <Grid size={{ xs: 3, sm: 1 }} key={key} >
                <Typography variant="caption" color="lightgray" fontSize="0.7rem">
                  {key}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: getStatColor(key, f[key]), fontWeight: 500 }}
                  fontSize="0.9rem"
                >
                  {f[key].toFixed(2)}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Dialog Filtri */}
      <Dialog open={filterOpen} onClose={() => setFilterOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="subtitle1">Filters</Typography>
          <IconButton size="small" onClick={() => setFilterOpen(false)}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers sx={{ bgcolor: "#141618", color: "white" }}>
          <Stack spacing={1}>
            {players.map((p) => {
              const available = availableRolesByPlayer[p.id] || [];
              if (available.length === 0) return null;

              return (
                <Box key={p.id} sx={{ pb: 1, borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                  <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
                    <Avatar sx={{ width: 28, height: 28, bgcolor: "#576574", fontSize: 13 }}>
                      {p.nickname.charAt(0).toUpperCase()}
                    </Avatar>

                    <Typography variant="body2" fontSize="0.85rem" sx={{ flexGrow: 1 }}>
                      {p.nickname}
                    </Typography>

                    {/* ✅ Checkbox per filtro giocatore */}
                    <Checkbox
                      size="small"
                      checked={selectedPlayers.includes(p.id)}
                      onChange={(e) => {
                        setSelectedPlayers((prev) =>
                          e.target.checked ? [...prev, p.id] : prev.filter((id) => id !== p.id)
                        );
                      }}
                      sx={{
                        color: "white",
                        '&.Mui-checked': { color: "#00b894" },
                        p: 0.2,
                      }}
                    />

                    <Button
                      size="small"
                      onClick={() =>
                        setRoleFilter((prev) => ({
                          ...prev,
                          [p.id]: [...available],
                        }))
                      }
                    >
                      All
                    </Button>
                    <Button
                      size="small"
                      onClick={() => setRoleFilter((prev) => ({ ...prev, [p.id]: [] }))}>
                      None
                    </Button>
                  </Stack>

                  <ToggleButtonGroup
                    size="small"
                    value={roleFilter[p.id] || []}
                    onChange={(_, newRoles) =>
                      setRoleFilter((prev) => ({ ...prev, [p.id]: newRoles }))
                    }
                    sx={{ flexWrap: "wrap", gap: 0.5 }}
                  >
                    {available.map((r) => (
                      <ToggleButton
                        key={r}
                        value={r}
                        sx={{
                          color: "white",
                          borderColor: "rgba(255,255,255,0.08)",
                          fontSize: "0.75rem",
                          "&.Mui-selected": {
                            bgcolor: roleColors[r] || "#444",
                          },
                        }}
                      >
                        {r}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                </Box>
              );
            })}
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1 }}>
          <Button onClick={resetFilters} size="small" color="inherit">
            Reset
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setFilterOpen(false)} size="small">
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
